import { Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import ScrollToTopButton from "../../components/feature/ScrollToTopButton";
import { useScrollReveal } from "../../hooks/useScrollReveal";
import SEO from "@/components/SEO";
import { membresias } from "@/lib/membresias";

const [adompretur, ompt] = membresias;

const pilares = [
  {
    icon: "ri-shield-check-line",
    title: "Rigor informativo",
    desc: "Cada artículo está respaldado por fuentes oficiales, datos verificados y análisis fundamentado. La credibilidad es el pilar de Aviación Insight RD.",
  },
  {
    icon: "ri-message-3-line",
    title: "Claridad en la comunicación",
    desc: "Traduce los tecnicismos de la aviación en un lenguaje claro y accesible para todos los públicos, desde el entusiasta hasta el profesional.",
  },
  {
    icon: "ri-compass-3-line",
    title: "Visión estratégica del sector",
    desc: "Análisis profundo de las tendencias del sector aeronáutico con perspectiva local, regional e internacional para anticipar el futuro.",
  },
];

export default function SobreMi() {
  const photoRef = useScrollReveal<HTMLDivElement>();
  const textRef = useScrollReveal<HTMLDivElement>();
  const affiliationsRef = useScrollReveal<HTMLDivElement>();
  const timelineRef = useScrollReveal<HTMLDivElement>();
  const siteUrl = import.meta.env.VITE_SITE_URL || window.location.origin;

  return (
    <main className="min-h-screen" style={{ fontFamily: "'Inter', sans-serif" }}>
      <SEO
        jsonLdSchemas={[
          {
            "@type": "AboutPage",
            name: "Sobre Aviación Insight RD",
            description: "El periódico digital especializado en el sector aeronáutico de República Dominicana y el Caribe.",
            url: `${siteUrl}/sobre-mi`,
          },
          {
            "@type": "Person",
            name: "Carmen Bretón",
            jobTitle: "Periodista y fundadora de Aviación Insight RD",
            description: "Periodista y fundadora de Aviación Insight RD, relacionista pública de la Junta de Aviación Civil.",
            url: `${siteUrl}/sobre-mi`,
            sameAs: [
              "https://www.facebook.com/profile.php?id=61591088725425",
              "https://www.instagram.com/aviacioninsightrd",
              "https://x.com/aviacioninsight",
            ],
            affiliation: [
              {
                "@type": "Organization",
                name: "Asociación Dominicana de Prensa Turística",
                alternateName: "ADOMPRETUR",
                url: adompretur.href,
              },
              {
                "@type": "Organization",
                name: "Organización Mundial de Periodismo Turístico",
                alternateName: "OMPT",
                url: ompt.href,
              },
            ],
            worksFor: {
              "@type": "Organization",
              name: "Junta de Aviación Civil",
            },
          },
        ]}
      />
      <Navbar />

      {/* Hero — Sobre el Proyecto */}
      <section className="relative min-h-[520px] flex items-end pb-0 overflow-hidden bg-[#0B1F3A]">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=aerial%20view%20aircraft%20wing%20over%20Caribbean%20turquoise%20sea%20islands%20from%20airplane%20window%2C%20beautiful%20blue%20ocean%20below%2C%20travel%20aviation%20perspective%2C%20professional%20photography%2C%20dreamy%20sky&width=1920&height=700&seq=sobre-mi-hero&orientation=landscape"
            alt="Sobre Aviación Insight RD"
            className="w-full h-full object-cover object-center opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B1F3A] via-[#0B1F3A]/70 to-[#0B1F3A]/40" />
        </div>

        <div className="relative z-10 w-full max-w-screen-xl mx-auto px-4 md:px-6 pt-40 pb-16">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-px w-10 bg-blue-400" />
              <span className="text-blue-400 text-sm font-inter font-bold tracking-widest uppercase">
                Sobre el proyecto
              </span>
            </div>
            <h1
              className="text-white font-bold leading-none mb-5"
              style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: "clamp(2.5rem, 6vw, 4.5rem)" }}
            >
              AVIACIÓN INSIGHT RD
            </h1>
            <p className="text-blue-200/90 font-inter text-xl mb-4">
              El periódico digital especializado en el sector aeronáutico de RD y el Caribe
            </p>
            <div className="space-y-4 text-slate-300 font-inter text-base leading-relaxed max-w-xl">
              <p>
                Desde la República Dominicana, damos seguimiento a la actualidad de la aviación, con especial atención al turismo y la carga aérea, dos actividades estrechamente vinculadas al desarrollo económico del país. Al mismo tiempo, ampliamos nuestra mirada hacia el Caribe para abordar los principales movimientos y tendencias de la industria.
              </p>
              <p>
                Un proyecto creado y dirigido por <strong className="text-white">Carmen Bretón</strong> desde el 21 de mayo de 2026, con el propósito de ofrecer información comprensible y con criterio profesional.
              </p>
            </div>

            {/* Tarjetas del proyecto en el hero */}
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-4 gap-4">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20">
                <div className="w-9 h-9 flex items-center justify-center bg-blue-500/30 rounded-lg mb-3">
                  <i className="ri-calendar-line text-blue-300 text-lg" />
                </div>
                <p className="text-blue-200 text-sm font-inter mb-1">Fundación</p>
                <p className="text-white font-bold text-base font-inter">21 de mayo de 2026</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20">
                <div className="w-9 h-9 flex items-center justify-center bg-blue-500/30 rounded-lg mb-3">
                  <i className="ri-map-pin-line text-blue-300 text-lg" />
                </div>
                <p className="text-blue-200 text-sm font-inter mb-1">Cobertura</p>
                <p className="text-white font-bold text-base font-inter">República Dominicana y el Caribe</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20">
                <div className="w-9 h-9 flex items-center justify-center bg-blue-500/30 rounded-lg mb-3">
                  <i className="ri-focus-3-line text-blue-300 text-lg" />
                </div>
                <p className="text-blue-200 text-sm font-inter mb-1">Enfoque</p>
                <p className="text-white font-bold text-base font-inter">Noticias, operaciones, seguridad y tendencias</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20">
                <div className="w-9 h-9 flex items-center justify-center bg-blue-500/30 rounded-lg mb-3">
                  <i className="ri-user-star-line text-blue-300 text-lg" />
                </div>
                <p className="text-blue-200 text-sm font-inter mb-1">Dirección</p>
                <p className="text-white font-bold text-base font-inter">Carmen Bretón — Periodista&nbsp;</p>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 mt-10">
              <Link
                to="/contacto"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 border border-white/30 text-white text-base font-inter font-semibold rounded-lg transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-mail-send-line" /> Contactar
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Sobre Carmen Bretón — con foto */}
      <section className="bg-white py-24">
        <div className="max-w-screen-xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Foto de Carmen */}
            <div ref={photoRef} className="reveal-image flex justify-center lg:justify-start">
              <div
                className="w-full max-w-xs md:w-72 rounded-2xl overflow-hidden"
                style={{
                  height: "clamp(360px, 55vw, 460px)",
                  background: "linear-gradient(160deg, #dbeafe 0%, #e0f2fe 50%, #f0f9ff 100%)",
                }}
              >
                <img
                  src="https://public.readdy.ai/ai/img_res/edited_0cb4fda0b26cd35fa40e530078c9dba1_13c5d6bf.jpg"
                  alt="Carmen Bretón - Aviación Insight RD"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Texto + Timeline */}
            <div ref={textRef} className="reveal-section">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-1 h-8 bg-blue-600 rounded-full" />
                <h2
                  className="text-[#0B1F3A] font-bold text-3xl md:text-4xl"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                >
                  SOBRE CARMEN BRETÓN
                </h2>
              </div>
              <div className="mb-8">
                <h3 className="text-[#0B1F3A] font-bold text-2xl font-inter">
                  Carmen Bretón
                </h3>
                <p className="text-blue-600 font-inter font-semibold text-base mt-1">
                  Periodista y fundadora de Aviación Insight RD
                </p>
              </div>
              <div className="space-y-5 text-slate-600 font-inter text-base leading-relaxed">
                <p>
                  Cuenta con trayectoria en medios de comunicación y agencias de relaciones públicas. Forma parte de la <strong className="text-[#0B1F3A]">Asociación Dominicana de Prensa Turística</strong> (ADOMPRETUR), institución en la que ha desempeñado diversos cargos, entre ellos encargada de Comunicaciones, directora de Relaciones Públicas y Comunicaciones, y secretaria general de la directiva 2025-2027.
                </p>
                <p>
                  Además, es miembro de la <strong className="text-[#0B1F3A]">Organización Mundial de Periodismo Turístico</strong> (OMPT).
                </p>
                <p>
                  Actualmente, se desempeña como <strong className="text-[#0B1F3A]">relacionista pública de la Junta de Aviación Civil</strong>. Anteriormente, ocupó el cargo de coordinadora de Relaciones Públicas y editora de redes sociales en la Dirección de Comunicación del Instituto Dominicano de Aviación Civil (IDAC).
                </p>
              </div>

              {/* Tarjetas de afiliaciones */}
              <div ref={affiliationsRef} className="reveal-group mt-10 grid grid-cols-1 sm:grid-cols-2 gap-5">
                <a
                  href={adompretur.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="reveal-scale block bg-[#F8F9FB] rounded-xl p-5 border border-slate-100 hover:border-blue-200 transition-colors cursor-pointer"
                >
                  <div className="h-24 flex items-center justify-center bg-white rounded-lg border border-slate-100 mb-4 p-3">
                    <img src={adompretur.logo} alt="Logo de ADOMPRETUR" className="max-h-full max-w-full object-contain" />
                  </div>
                  <p className="text-slate-500 text-sm font-inter mb-1">Afiliación</p>
                  <p className="text-[#0B1F3A] font-bold text-base font-inter">Asociación Dominicana de Prensa Turística</p>
                  <p className="text-slate-400 text-sm font-inter mt-1">ADOMPRETUR — Secretaria General 2025-2027</p>
                </a>
                <a
                  href={ompt.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="reveal-scale block bg-[#F8F9FB] rounded-xl p-5 border border-slate-100 hover:border-blue-200 transition-colors cursor-pointer"
                >
                  <div className="h-24 flex items-center justify-center bg-white rounded-lg border border-slate-100 mb-4 p-3">
                    <img src={ompt.logo} alt="Logo de la OMPT" className="max-h-full max-w-full object-contain" />
                  </div>
                  <p className="text-slate-500 text-sm font-inter mb-1">Afiliación internacional</p>
                  <p className="text-[#0B1F3A] font-bold text-base font-inter">Organización Mundial de Periodismo Turístico</p>
                  <p className="text-slate-400 text-sm font-inter mt-1">OMPT — Miembro activo</p>
                </a>
              </div>

              {/* Línea de tiempo profesional */}
              <div ref={timelineRef} className="reveal-group mt-12 bg-[#F8F9FB] rounded-2xl p-8 border border-slate-100">
                <h4 className="text-[#0B1F3A] font-bold text-lg font-inter mb-6">
                  Trayectoria profesional
                </h4>
                <div className="space-y-6 relative">
                  <div className="timeline-line-draw absolute left-[19px] top-2 bottom-2 w-px bg-blue-200" />

                  <div className="reveal-timeline flex gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-blue-600 rounded-full flex-shrink-0 relative z-10 timeline-dot-pulse">
                      <i className="ri-briefcase-line text-white text-base" />
                    </div>
                    <div>
                      <p className="text-blue-600 font-semibold text-sm font-inter">Actualidad</p>
                      <p className="text-[#0B1F3A] font-bold text-base font-inter">Relacionista Pública</p>
                      <p className="text-slate-500 font-inter text-sm">Junta de Aviación Civil</p>
                    </div>
                  </div>

                  <div className="reveal-timeline flex gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-blue-500/80 rounded-full flex-shrink-0 relative z-10">
                      <i className="ri-rocket-line text-white text-base" />
                    </div>
                    <div>
                      <p className="text-blue-600 font-semibold text-sm font-inter">Proyecto propio</p>
                      <p className="text-[#0B1F3A] font-bold text-base font-inter">Fundadora de Aviación Insight RD</p>
                      <p className="text-slate-500 font-inter text-sm">El periódico digital especializado en el sector aeronáutico de República Dominicana y el Caribe</p>
                    </div>
                  </div>

                  <div className="reveal-timeline flex gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-blue-400/60 rounded-full flex-shrink-0 relative z-10">
                      <i className="ri-edit-line text-white text-base" />
                    </div>
                    <div>
                      <p className="text-blue-600 font-semibold text-sm font-inter">Anteriormente</p>
                      <p className="text-[#0B1F3A] font-bold text-base font-inter">Coordinadora de RRPP y Editora de Redes Sociales</p>
                      <p className="text-slate-500 font-inter text-sm">Dirección de Comunicación — IDAC</p>
                    </div>
                  </div>

                  <div className="reveal-timeline flex gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-blue-300/50 rounded-full flex-shrink-0 relative z-10">
                      <i className="ri-newspaper-line text-white text-base" />
                    </div>
                    <div>
                      <p className="text-blue-600 font-semibold text-sm font-inter">Formación</p>
                      <p className="text-[#0B1F3A] font-bold text-base font-inter">Lic. en Comunicación Social, mención Periodismo</p>
                      <p className="text-slate-500 font-inter text-sm">Universidad Autónoma de Santo Domingo (UASD)</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pilares */}
      <section className="bg-[#F8F9FB] py-20">
        <div className="max-w-screen-xl mx-auto px-4 md:px-6">
          <div className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-px w-10 bg-blue-500" />
              <span className="text-blue-600 text-sm font-inter font-bold tracking-widest uppercase">Filosofía de trabajo</span>
              <div className="h-px w-10 bg-blue-500" />
            </div>
            <h2
              className="text-[#0B1F3A] font-bold"
              style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: "2.6rem" }}
            >
              LOS PILARES DE AVIACIÓN INSIGHT RD
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {pilares.map((p) => (
              <div key={p.title} className="bg-white rounded-2xl p-8 border border-slate-100">
                <div className="w-14 h-14 flex items-center justify-center bg-blue-50 rounded-xl mb-5">
                  <i className={`${p.icon} text-blue-600 text-2xl`} />
                </div>
                <h3 className="text-[#0B1F3A] font-bold text-lg font-inter mb-3">{p.title}</h3>
                <p className="text-slate-500 font-inter text-base leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contacto CTA */}
      <section className="bg-[#0B1F3A] py-20">
        <div className="max-w-2xl mx-auto px-4 md:px-6 text-center">
          <div className="w-16 h-16 flex items-center justify-center bg-blue-600/30 rounded-full mx-auto mb-6">
            <i className="ri-mail-send-line text-blue-300 text-3xl" />
          </div>
          <h2
            className="text-white font-bold mb-4"
            style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: "2.4rem" }}
          >
            ¿QUIERES CONECTAR?
          </h2>
          <p className="text-blue-200/80 font-inter text-base mb-10">
            Para entrevistas, colaboraciones, publicidad o simplemente para saludar: respondemos personalmente cada mensaje.
          </p>
          <Link
            to="/contacto"
            className="inline-flex items-center gap-2 px-10 py-4 bg-blue-600 hover:bg-blue-500 text-white font-inter font-semibold text-base rounded-lg transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-send-plane-line" />
            Ir a la página de Contacto
          </Link>
        </div>
      </section>

      <Footer />
      <ScrollToTopButton />
    </main>
  );
}